import { init, track } from '@plausible-analytics/tracker';

/**
 * Plausible Analytics Service
 * Provides methods to track page views and custom events.
 * Uses a singleton pattern to ensure initialization only happens once.
 */

export type AnalyticsEvent =
  | 'appLoaded' // Event for when the app is fully loaded
  | 'clickWidgetOpenButton' // Event for when the widget is opened by clicking the widget button
  | 'cookieNotificationClosed' // Event for when the cookie notification is closed
  | `external:${string}` // External tracking events. Currently used for Sekasin events.
  | 'helpCardOpened' // Event for when a help card is opened
  | 'informationCardOpened' // Event for when an information card is opened
  | 'leftArrowClicked' // Event for when left arrow is clicked in the informationCard carousel
  | 'openCookieNotificationHyvaTieta' // Event for when the cookie notification additional info is opened
  | 'playButtonClicked' // Event for when the informationCard audio play button is clicked
  | 'rightArrowClicked' // Event for when right arrow is clicked in the informationCard carousel
  | 'speechToTextRecordingStarted' // Event for when user starts voice recording
  | 'speechToTextRecordingFinished' // Event for when user stops recording before completion
  | 'speechToTextMaxDurationReached' // Event for when recording auto-stopped at max duration
  | 'speechToTextTranscriptionSuccess' // Event for when transcription is successful
  | 'speechToTextTranscriptionSentEdited' // Event for when user sent transcribed text after editing
  | 'speechToTextTranscriptionSentAsIs' // Event for when user sent transcribed text without edits
  | 'suicidalThoughtsConfirmed' // Event for when user confirms suicidal thoughts
  | 'suicidalThoughtsDenied' // Event for when user denies suicidal thoughts
  | 'suicidalThoughtsPageViewed' // Event for when a suicide flow page is shown
  | 'suicidalThoughtsOptionSelected' // Event for when a suicide flow option is selected (legacy, kept for backwards compatibility)
  | 'suicidalThoughtsLowRiskSelected' // Event for when user selects "I have suicidal thoughts, and I don't know how to deal with them"
  | 'suicidalThoughtsMediumRiskSelected' // Event for when user selects "I have a suicide plan that I might carry out someday"
  | 'suicidalThoughtsHighRiskSelected' // Event for when user selects "I feel unbearable. I'm planning to commit suicide"
  | 'suicidalThoughtsChatLinkClicked' // Event for when user clicks a suicide flow chat link
  | 'suicidalThoughtsContinueClicked' // Event for when user continues to chat
  | 'suicidalThoughtsBackClicked' // Event for when user clicks back within suicide flow
  | 'suicidalThoughtsTriggered' // Event for when suicide questions modal is shown
  | 'topicChanged' // Event for when topic was changed from topic change dropdown
  | 'returnCodeSubmitted' // Event for when a return code is submitted in the intake tool
  | 'returnCodeSubmitSuccess' // Event for when return code submission succeeds
  | 'returnCodeSubmitFailed' // Event for when return code submission fails
  | 'widgetCloseButtonClicked' // Event for when the widget close button is clicked
  | 'widgetMinimizeButtonClicked' // Event for when the widget minimize button is clicked
  | 'widgetMinimizeFunctionCalled'; // Event for when the widget is minimized programmatically

interface AnalyticsTrackOptions {
  eventName: AnalyticsEvent;
  /* eslint-disable  @typescript-eslint/no-explicit-any */
  properties?: Record<string, any>;
}

class AnalyticsService {
  private static instance: AnalyticsService;
  private isInitialized = false;

  private constructor() {}

  public static getInstance(): AnalyticsService {
    if (!AnalyticsService.instance) {
      AnalyticsService.instance = new AnalyticsService();
    }
    return AnalyticsService.instance;
  }

  /**
   * Initialize Plausible Analytics. Can only be called once.
   * Subsequent calls will be ignored to prevent multiple initializations.
   */
  public init(configId: string): void {
    if (this.isInitialized) {
      return;
    }

    init({
      domain: 'chatbot.goodlabs.net',
      outboundLinks: true,
      fileDownloads: true,
      customProperties: { configId },
    });

    this.isInitialized = true;
  }

  /**
   * Track a custom event.
   * @param {AnalyticsTrackOptions} options - Object containing eventName and optional properties.
   */
  public trackEvent({ eventName, properties = {} }: AnalyticsTrackOptions): void {
    if (!this.isInitialized) {
      console.warn('Analytics service not initialized. Call init() first before tracking events.');
      return;
    }

    track(eventName, { props: properties });
  }
}

// Export a singleton instance
const analyticsService = AnalyticsService.getInstance();

export default analyticsService;
